import { useEffect } from 'react';
import { getRefreshToken, tokenNeedsRefresh, forceLogoutAndRedirect, isTokenKnownExpired } from '../utils/auth';
import { refreshAndPersistTokens } from '../utils/httpApi';
import { debugLog, debugError } from '../utils/debug';
import { diag } from '../utils/diag';

// How often to re-check the access token while the app is open. The access
// token lives 24h and we refresh in its final hour, so a 5-minute cadence is
// far more than fine — it just needs to be short enough that the window is
// never missed during a normally-open tab.
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Keeps the session alive by proactively rotating the access token before it
 * expires, instead of waiting for a reactive HTTP 401 or a WebSocket auth-close
 * (both of which are unreliable — the Live page is WebSocket-only and never
 * issues an authenticated HTTP request, so its access token used to silently
 * expire at the 24h mark and the whole 7-day sliding window never advanced).
 *
 * The backend reissues a fresh 7-day refresh token on every refresh, so each
 * successful rotation slides the window forward. When a refresh fails (the
 * refresh token itself has expired after 7 idle days, or the account was
 * disabled), we force a logout+redirect — this is the SINGLE session-death
 * detector that works on every page, including the WebSocket-only Live map
 * that previously just sat there broken instead of returning to login.
 *
 * Checks run on: mount, a 5-minute interval, tab re-focus, and tab visibility
 * regain (the last two catch a laptop resuming from overnight sleep, where the
 * access token may already have expired but the 7-day refresh token is still
 * valid).
 *
 * @param {boolean} isAuthenticated
 */
export default function useProactiveTokenRefresh(isAuthenticated) {
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    // Prevent overlapping checks (interval + visibility firing together).
    let inFlight = false;

    const maybeRefresh = async (trigger) => {
      if (cancelled || inFlight) return;
      // No refresh token means the session can never be renewed. That is
      // harmless while the access token is still good, so we leave the normal
      // login flow to handle it — but once the access token has demonstrably
      // expired there is no way back and staying put is the bug: every request
      // then returns an empty HTTP 200 (the backend does not 401 a dead token),
      // so the app renders blank forever instead of returning to login.
      if (!getRefreshToken()) {
        if (isTokenKnownExpired()) {
          debugError('❌ Access token expired and there is no refresh token — logging out');
          diag('TOKEN_EXPIRED_NO_REFRESH_LOGOUT', { trigger });
          forceLogoutAndRedirect('access token expired and no refresh token');
        }
        return;
      }
      if (!tokenNeedsRefresh()) return;

      inFlight = true;
      try {
        debugLog(`🔄 Proactive token refresh (trigger: ${trigger})`);
        diag('TOKEN_REFRESH_ATTEMPT', { trigger });
        // refreshAndPersistTokens is single-flight-shared with the HTTP 401
        // path and the WebSocket reauth path, so this never races them.
        const newToken = await refreshAndPersistTokens();
        if (cancelled) return;
        if (!newToken) {
          debugError('❌ Proactive refresh failed — session is dead, logging out');
          diag('TOKEN_REFRESH_FAIL_LOGOUT', { trigger });
          forceLogoutAndRedirect('proactive token refresh failed');
        } else {
          debugLog('✅ Proactive token refresh succeeded — session extended');
          diag('TOKEN_REFRESH_OK', { trigger });
        }
      } finally {
        inFlight = false;
      }
    };

    // Check immediately on mount (covers a reload after a long time away).
    maybeRefresh('mount');

    const intervalId = setInterval(() => maybeRefresh('interval'), CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) maybeRefresh('visibility');
    };
    const onFocus = () => maybeRefresh('focus');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated]);
}
